import { Fragment } from 'react';
import { useWizard, type WizardConfig } from '../wizardContext';
import { LGPD_LABEL } from '../types';
import { ageFromDate, isValidCPF, isValidEmail, isValidName, maskCPF, maskPhone, onlyDigits } from '../utils/finance';
import { resolveRedirectHref, resolveRedirectTarget } from '../utils/redirect';
import { sanitizeHtml } from '../../../utils/sanitize';
import { AlertIcon } from '../parts/icons';

const CONTACT_OPTIONS = [
  { label: 'E-mail', value: 'email' },
  { label: 'WhatsApp', value: 'whatsapp' },
  { label: 'Telefone', value: 'telefone' },
];

const validate = (
  p: ReturnType<typeof useWizard>['state']['personal'],
  config: WizardConfig,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!isValidName(p.name)) errors.name = 'Informe seu nome completo.';
  if (!isValidEmail(p.email)) errors.email = 'Informe um e-mail válido.';
  if (onlyDigits(p.phone).length < 10) errors.phone = 'Informe um telefone válido.';
  /* Campo exibido = campo obrigatório. */
  if (config.showCpf && !isValidCPF(p.cpf)) {
    errors.cpf = 'CPF inválido. Confira os números digitados.';
  }
  if (config.showBirthdate) {
    const age = ageFromDate(p.birthdate);
    if (age == null) errors.birthdate = 'Informe sua data de nascimento.';
    else if (age < 18) errors.birthdate = 'É necessário ter 18 anos ou mais para simular um financiamento.';
    else if (age > 100) errors.birthdate = 'Confira a data de nascimento informada.';
  }
  if (!p.contactPrefs.length) errors.contact = 'Escolha pelo menos uma forma de contato.';
  if (!p.lgpdAccepted) errors.lgpd = 'É necessário aceitar para continuar.';
  return errors;
};

const PrivacyLabel = ({ href, target, text }: { href: string; target?: '_blank'; text: string }) => {
  const parts = text.split('Política de privacidade');
  return (
    <span>
      {parts.map((part, i) => (
        <Fragment key={i}>
          {part}
          {i < parts.length - 1 && (
            <a href={href} target={target} rel={target === '_blank' ? 'noopener noreferrer' : undefined}>
              Política de privacidade
            </a>
          )}
        </Fragment>
      ))}
    </span>
  );
};

export const Step2Dados = ({ onComplete }: { onComplete?: () => void }) => {
  const { state, set, next, prev, config } = useWizard();
  const p = state.personal;
  const errors = state.errors;

  const privacyHref = resolveRedirectHref(config.privacyRedirect) || config.privacyPolicyUrl || '#';
  const privacyTarget = config.privacyRedirect
    ? resolveRedirectTarget(config.privacyRedirect)
    : config.privacyPolicyUrl
      ? '_blank'
      : undefined;

  const clearErr = (key: string) =>
    set((d) => {
      if (d.errors[key]) delete d.errors[key];
    });

  const onContinue = () => {
    const errs = validate(p, config);
    if (Object.keys(errs).length) {
      set((d) => { d.errors = errs; });
      return;
    }
    // Conversão da 1ª etapa (dados pessoais) — LGPD já aceito acima.
    onComplete?.();
    next();
  };

  const fieldClass = (key: string, valid?: boolean) =>
    `lpfs-field${errors[key] ? ' is-error' : valid ? ' is-valid' : ''}`;

  const textField = (
    key: 'name' | 'email' | 'phone' | 'birthdate',
    label: string,
    type: string,
    extraClass = '',
    mask?: (v: string) => string,
  ) => (
    <div className={`${fieldClass(key)} ${extraClass}`.trim()}>
      <label className="lpfs-label" htmlFor={`lpfs-${key}`}>
        {label}
      </label>
      <input
        className="lpfs-input"
        id={`lpfs-${key}`}
        type={type}
        autoComplete="off"
        value={p[key]}
        onChange={(e) => {
          const val = mask ? mask(e.target.value) : e.target.value;
          set((d) => { d.personal[key] = val; });
          clearErr(key);
        }}
      />
      {errors[key] && (
        <div className="lpfs-error-msg">
          <AlertIcon />
          <span>{errors[key]}</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="lpfs-anim-in">
      <div className="lpfs-step-head">
        <h2 className="lpfs-step-title">Seus dados</h2>
        <p className="lpfs-step-sub">Precisamos de algumas informações para preparar sua simulação.</p>
      </div>

      <div className="lpfs-form-grid">
        {textField('name', 'Nome completo', 'text', 'lpfs-col-2')}
        {textField('email', 'E-mail', 'email')}
        {textField('phone', 'Telefone / WhatsApp', 'tel', '', maskPhone)}
        {config.showCpf && (
          <div className={fieldClass('cpf')}>
            <label className="lpfs-label" htmlFor="lpfs-cpf">
              CPF
            </label>
            <input
              className="lpfs-input"
              id="lpfs-cpf"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={p.cpf}
              onChange={(e) => {
                const val = maskCPF(e.target.value);
                set((d) => { d.personal.cpf = val; });
                clearErr('cpf');
              }}
            />
            {errors.cpf && (
              <div className="lpfs-error-msg">
                <AlertIcon />
                <span>{errors.cpf}</span>
              </div>
            )}
          </div>
        )}
        {config.showBirthdate && textField('birthdate', 'Data de nascimento', 'date')}
      </div>

      <div className={`${fieldClass('contact')} lpfs-col-2`}>
        <label className="lpfs-label">
          Quero receber contato por <span className="lpfs-label-hint">selecione um ou mais</span>
        </label>
        <div className="lpfs-checkgroup" role="group" aria-label="Preferência de contato">
          {CONTACT_OPTIONS.map((opt) => {
            const on = p.contactPrefs.includes(opt.value);
            return (
              <label className="lpfs-native-option" key={opt.value}>
                <input
                  type="checkbox"
                  className="lpfs-native-check"
                  checked={on}
                  onChange={() => {
                    set((d) => {
                      if (on) d.personal.contactPrefs = d.personal.contactPrefs.filter((x) => x !== opt.value);
                      else d.personal.contactPrefs = [...d.personal.contactPrefs, opt.value];
                    });
                    clearErr('contact');
                  }}
                />
                <span>{opt.label}</span>
              </label>
            );
          })}
        </div>
        {errors.contact && (
          <div className="lpfs-error-msg">
            <AlertIcon />
            <span>{errors.contact}</span>
          </div>
        )}
      </div>

      <div className="lpfs-field lpfs-col-2">
        <label className="lpfs-label">Pretende usar um seminovo na negociação?</label>
        <div className="lpfs-radiogroup-inline" role="radiogroup" aria-label="Pretende usar um seminovo na negociação?">
          {[
            { label: 'Sim', val: true },
            { label: 'Não', val: false },
          ].map((opt) => (
            <label className="lpfs-native-option" key={opt.label}>
              <input
                type="radio"
                className="lpfs-native-radio"
                name="lpfs-has-trade-in"
                checked={state.hasTradeIn === opt.val}
                onChange={() => set((d) => { d.hasTradeIn = opt.val; })}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className={`${fieldClass('lgpd')} lpfs-col-2`} style={{ marginTop: 8 }}>
        <label className="lpfs-check">
          <input
            type="checkbox"
            className="lpfs-native-check"
            checked={p.lgpdAccepted}
            onChange={(e) => {
              const checked = e.target.checked;
              set((d) => { d.personal.lgpdAccepted = checked; });
              clearErr('lgpd');
            }}
          />
          <PrivacyLabel href={privacyHref} target={privacyTarget} text={LGPD_LABEL} />
        </label>
        {errors.lgpd && (
          <div className="lpfs-error-msg">
            <AlertIcon />
            <span>{errors.lgpd}</span>
          </div>
        )}
        {config.privacyPolicyText && (
          <div className="lpfs-lgpd-text" data-af-field="privacyPolicyText" dangerouslySetInnerHTML={{ __html: sanitizeHtml(config.privacyPolicyText) }} />
        )}
      </div>

      <div className="lpfs-actions">
        <button type="button" className="lpfs-btn lpfs-btn-ghost" onClick={prev}>
          Voltar
        </button>
        <span className="lpfs-spacer" />
        <button type="button" className="lpfs-btn lpfs-btn-primary" onClick={onContinue}>
          Continuar
        </button>
      </div>
    </div>
  );
};
